import { isFinite } from 'lodash';
import { useCallback } from 'react';
import { TbLayoutGrid, TbList } from 'react-icons/tb';
import type { URLSearchParamsInit } from 'react-router-dom';

import type { RadioButtonItemProps } from 'components-new/radio-navigation/radio-button-item';
import { createLocale } from 'locales';

const localize = createLocale('organizationWeekPlannerLocales');

export type OrganizationSearchParams = {
  fromDate: string | null;
  toDate: string | null;
  viewRadioButtonElements: RadioButtonItemProps[];
  viewType: 'grid' | 'table';
  selectedFreelancers: Array<number>;
  selectedLocations: Array<number>;
  selectedOrganizations: Array<number>;
  onFreelancersQueryChange: (query: string | null) => void;
  onLocationsQueryChange: (query: string | null) => void;
  onOrganizationsQueryChange: (query: string | null) => void;
  onSelectedFreelancersChange: (ids: Array<number>) => void;
  onSelectedOrganizationsChange: (ids: Array<number>) => void;
  onSelectedLocationsChange: (ids: Array<number>) => void;
  freelancersQuery: string | null;
  locationsQuery: string | null;
  organizationsQuery: string | null;
  jobStatus?: 'open' | 'assigned';
  setJobStatus: (status: 'open' | 'assigned' | undefined) => void;
  jobType?: 'interest' | 'urgent' | 'personal' | undefined;
  setJobType: (type: 'interest' | 'urgent' | 'personal') => void;
  resetPageNumber: () => void;
};

export const organizationSearchParamsInitialValue: OrganizationSearchParams = {
  freelancersQuery: null,
  fromDate: null,
  locationsQuery: null,
  onFreelancersQueryChange: (_) => {},
  onLocationsQueryChange: (_) => {},
  onOrganizationsQueryChange: (_) => {},
  onSelectedFreelancersChange: (_) => {},
  onSelectedLocationsChange: (_) => {},
  onSelectedOrganizationsChange: (_) => {},
  organizationsQuery: null,
  resetPageNumber: () => {},
  selectedFreelancers: [],
  selectedLocations: [],
  selectedOrganizations: [],
  setJobStatus: (_) => {},
  setJobType: (_) => {},
  toDate: null,
  viewRadioButtonElements: [],
  viewType: 'grid',
};

const DEFAULT_PAGE = '1';

const useOrganizationPlannerSearchParams = (plannerSearchParams: {
  searchParams: URLSearchParams;
  setSearchParams: (
    nextInit: URLSearchParamsInit,
    navigateOptions?:
      | {
          replace?: boolean | undefined;
          state?: unknown;
        }
      | undefined,
  ) => void;
  currentSearchParams: Record<string, string>;
}): OrganizationSearchParams => {
  const { currentSearchParams, searchParams, setSearchParams } =
    plannerSearchParams;
  const onSelectedFreelancersChange = useCallback(
    (ids: Array<number>) => {
      if (ids.length === 0) {
        searchParams.delete('selectedFreelancers');
        searchParams.set('pageNumber', DEFAULT_PAGE);
        setSearchParams(searchParams);
      } else {
        setSearchParams({
          ...currentSearchParams,
          pageNumber: DEFAULT_PAGE,
          selectedFreelancers: ids.join(','),
        });
      }
    },
    [searchParams, setSearchParams, currentSearchParams],
  );

  const onSelectedOrganizationsChange = useCallback(
    (ids: Array<number>) => {
      searchParams.set('pageNumber', DEFAULT_PAGE);
      if (ids.length === 0) {
        searchParams.delete('selectedOrganizations');
        searchParams.set('pageNumber', DEFAULT_PAGE);
        setSearchParams(searchParams);
      } else {
        setSearchParams({
          ...currentSearchParams,
          pageNumber: DEFAULT_PAGE,
          selectedOrganizations: ids.join(','),
        });
      }
    },
    [setSearchParams, currentSearchParams, searchParams],
  );

  const onSelectedLocationsChange = useCallback(
    (ids: Array<number>) => {
      searchParams.set('pageNumber', DEFAULT_PAGE);
      if (ids.length === 0) {
        searchParams.delete('selectedLocations');
        searchParams.set('pageNumber', DEFAULT_PAGE);
        setSearchParams(searchParams);
      } else {
        setSearchParams({
          ...currentSearchParams,
          pageNumber: DEFAULT_PAGE,
          selectedLocations: ids.join(','),
        });
      }
    },
    [setSearchParams, currentSearchParams, searchParams],
  );

  const switchViewType = useCallback(() => {
    setSearchParams({
      ...currentSearchParams,
      viewType: currentSearchParams.viewType === 'grid' ? 'table' : 'grid',
    });
  }, [currentSearchParams, setSearchParams]);

  const onFreelancersQueryChange = useCallback(
    (query: string | null) => {
      if (query) {
        setSearchParams({
          ...currentSearchParams,
          freelancersQuery: query,
        });
      } else {
        searchParams.delete('freelancersQuery');
        setSearchParams(searchParams);
      }
    },
    [currentSearchParams, searchParams, setSearchParams],
  );
  const onLocationsQueryChange = useCallback(
    (query: string | null) => {
      if (query) {
        setSearchParams({
          ...currentSearchParams,
          locationsQuery: query,
        });
      } else {
        searchParams.delete('locationsQuery');
        setSearchParams(searchParams);
      }
    },
    [currentSearchParams, searchParams, setSearchParams],
  );
  const onOrganizationsQueryChange = useCallback(
    (query: string | null) => {
      if (query) {
        setSearchParams({
          ...currentSearchParams,
          organizationsQuery: query,
        });
      } else {
        searchParams.delete('organizationsQuery');
        setSearchParams(searchParams);
      }
    },
    [currentSearchParams, searchParams, setSearchParams],
  );

  const {
    freelancersQuery,
    jobType,
    jobStatus,
    locationsQuery,
    organizationsQuery,
    viewType,
    selectedFreelancers,
    selectedLocations,
    selectedOrganizations,
  } = {
    freelancersQuery: searchParams.get('freelancersQuery'),
    jobStatus: searchParams.get('jobStatus') as 'open' | 'assigned' | undefined,
    jobType: searchParams.get('jobType') as
      | 'interest'
      | 'urgent'
      | 'personal'
      | undefined,
    locationsQuery: searchParams.get('locationsQuery'),
    organizationsQuery: searchParams.get('organizationsQuery'),
    selectedFreelancers: (searchParams.get('selectedFreelancers') ?? '')
      .split(',')
      .map((v) => parseInt(v, 10))
      .filter((v) => isFinite(v)),
    selectedLocations: (searchParams.get('selectedLocations') ?? '')
      .split(',')
      .map((v) => parseInt(v, 10))
      .filter((v) => isFinite(v)),
    selectedOrganizations: (searchParams.get('selectedOrganizations') ?? '')
      .split(',')
      .map((v) => parseInt(v, 10))
      .filter((v) => isFinite(v)),
    viewType: searchParams.get('viewType') as 'grid' | 'table',
  };

  const viewRadioButtonElements: RadioButtonItemProps[] = [
    {
      active: viewType === 'table',
      id: '1',
      label: localize('table'),
      leftIconProps: { icon: TbList },
      onClick: switchViewType,
    },
    {
      active: viewType === 'grid',
      id: '2',
      label: localize('grid'),
      leftIconProps: { icon: TbLayoutGrid },
      onClick: switchViewType,
    },
  ];

  const setJobStatus = useCallback(
    (status: 'open' | 'assigned' | undefined) => {
      let newStatus: 'open' | 'assigned' | undefined;

      if (!currentSearchParams.jobStatus) {
        newStatus = status === 'open' ? 'assigned' : 'open';
      } else if (status !== currentSearchParams.jobStatus) {
        searchParams.delete('jobStatus');
        setSearchParams(searchParams);
        return;
      } else {
        newStatus = status === 'open' ? 'assigned' : 'open';
      }

      setSearchParams({
        ...currentSearchParams,
        jobStatus: newStatus,
        pageNumber: DEFAULT_PAGE,
      });
    },
    [currentSearchParams, setSearchParams, searchParams],
  );

  const setJobType = useCallback(
    (type: 'interest' | 'urgent' | 'personal') => {
      let newType: 'interest' | 'urgent' | 'personal' | undefined;

      if (!currentSearchParams.jobType) {
        newType = type;
      } else if (type === currentSearchParams.jobType) {
        searchParams.delete('jobType');
        setSearchParams(searchParams);
        return;
      } else {
        newType = type;
      }

      setSearchParams({
        ...currentSearchParams,
        jobType: newType,
        pageNumber: DEFAULT_PAGE,
      });
    },
    [currentSearchParams, setSearchParams, searchParams],
  );

  const resetPageNumber = () => {
    setSearchParams({
      ...currentSearchParams,
      pageNumber: '1',
    });
  };

  const { fromDate, toDate } = {
    fromDate:
      searchParams.get('viewType') === 'table'
        ? searchParams.get('tableFromDate')
        : searchParams.get('fromDate'),
    toDate:
      searchParams.get('viewType') === 'table'
        ? searchParams.get('tableToDate')
        : searchParams.get('toDate'),
  };

  return {
    freelancersQuery,
    fromDate,
    jobStatus,
    jobType,
    locationsQuery,
    onFreelancersQueryChange,
    onLocationsQueryChange,
    onOrganizationsQueryChange,
    onSelectedFreelancersChange,
    onSelectedLocationsChange,
    onSelectedOrganizationsChange,
    organizationsQuery,
    resetPageNumber,
    selectedFreelancers,
    selectedLocations,
    selectedOrganizations,
    setJobStatus,
    setJobType,
    toDate,
    viewRadioButtonElements,
    viewType,
  };
};

export default useOrganizationPlannerSearchParams;
